import React, { useEffect, useState } from 'react';
import { ApolloProvider } from '@apollo/client/react';
import SnackbarAlert from './components/SnackbarAlert';
import queryString from 'query-string';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';

import './App.css';
import TabComponent from './components/TabComponent';
import { client } from './components/ApolloClient.js';
import { config } from './config';
import { appendCleaningAreas, setArea } from './state/actions-creators';
import { useAuth } from './hooks/useAuth';

const AppDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 480px;
  background-color: #fff;
`;

const LargeHeader = styled.h1`
  font-size: calc(1px + 7vmin);
`;

const Page = styled.div`
  width: 40vw;
`;

const App = () => {
  const { token } = useAuth();
  const { cleaningAreas } = useSelector((state) => state);
  const dispatch = useDispatch();
  const parsed = queryString.parse(window.location.search);
  const pathIds = window.location.pathname.split('/');
  const [apolloClient, setApolloClient] = useState();

  const checkLastEvent = (responseArea) => {
    /*  Check most recent event type (start/stop). 
          If start, save info of current cleaning progress.
    */
    fetch(`${config.lnt_api_url}/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orgId: pathIds[1],
        locationId: pathIds[2],
        area: responseArea,
        token: token,
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.type === 'start') {
          if (cleaningAreas.includes(json.area) === false)
            dispatch(appendCleaningAreas(json.area));
        }
        // Note: if json.type is null, there are no events for this area yet
        return json.area;
      })
      .catch((err) => {
        console.log('Error in App.js, calling /v1/check');
        console.log(err);
      });
  };

  const findAreasInProgress = () => {
    /*  Get areas currently in progress (last event = 'start') for the organization/location */
    fetch(`${config.lnt_api_url}/areas-in-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orgId: pathIds[1],
        locationId: pathIds[2],
        token: token,
      }),
    })
      .then((res) => res.json())
      .then((areasInProgress) => {
        // Directly set all areas in progress without individual checks
        areasInProgress.forEach((area) => {
          if (cleaningAreas.includes(area) === false) {
            dispatch(appendCleaningAreas(area));
          }
        });
      })
      .catch((err) => {
        console.log('Error in App.js, calling /v1/areas-in-progress');
        console.log(err);
      });
  };

  const findAreas = () => {
    /*  Get each area from DB for the organization/location 
        DEPRECATED: This was calling /check for each area, now replaced by findAreasInProgress
    */
    fetch(`${config.lnt_api_url}/find`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orgId: pathIds[1],
        locationId: pathIds[2],
        token: token,
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        json.forEach((area) => checkLastEvent(area));
      })

      .catch((err) => {
        console.log('Error in App.js, calling /v1/find');
        console.log(err);
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetch(`${config.lnt_api_url}/init?id=${parsed.id}`, {
        /* Initialize app: Fetch area name from API */
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orgId: pathIds[1],
          locationId: pathIds[2],
          token: token,
        }),
      })
        .then((res) => res.json())
        .then((json) => {
          dispatch(setArea(json.area));
          return json.area;
        })
        .catch((err) => {
          console.log('Error in App.js, calling /v1/type');
          console.log(err);
        });
      // Wait for initialize fetch to finish, then call
      findAreasInProgress();
    };
    fetchData();

    const initApolloClient = async () => {
      const apolloClient = await client;
      setApolloClient(apolloClient);
    };
    initApolloClient();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!apolloClient) return null;

  return (
    <ApolloProvider client={apolloClient}>
      <AppDiv>
        <LargeHeader>Smartcleaning App</LargeHeader>
        <Page>
          <TabComponent />
        </Page>
        <SnackbarAlert></SnackbarAlert>
      </AppDiv>
    </ApolloProvider>
  );
};

export default App;
